// frontend/src/components/Empresas/Empresas.tsx

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicNav } from '../layout/PublicNav/PublicNav';
import './Empresas.css';

interface Fact {
  icon: string;
  value: string;
  label: string;
}

const FACTS: Fact[] = [
  { icon: '🎯', value: '4', label: 'tipos de problema' },
  { icon: '🤖', value: '15', label: 'modelos disponíveis' },
  { icon: '🧩', value: '8', label: 'etapas automatizadas' },
  { icon: '🔐', value: '100%', label: 'auditável por trace_id' },
];

interface Benefit {
  icon: string;
  title: string;
  description: string;
}

const BENEFITS: Benefit[] = [
  { icon: '📤', title: 'Sem cientista de dados', description: 'Suba um CSV. A plataforma entende o que precisa ser previsto, escolhe o modelo e entrega a predição.' },
  { icon: '⚡', title: 'Do arquivo à decisão em minutos', description: 'O pipeline executa as 8 etapas automaticamente: ingestão, diagnóstico, decisão, treino, avaliação e predição.' },
  { icon: '🎯', title: '4 tipos de problema', description: 'Classificação (sim/não), regressão (quanto), série temporal (quando) e contagem (quantos). Cada um com métrica e limiar próprios.' },
  { icon: '🤖', title: '15 modelos testados', description: 'Regressão linear, logística, Random Forest, XGBoost, ARIMA, Prophet, LSTM e mais. O melhor modelo é escolhido automaticamente.' },
  { icon: '📋', title: 'Governança inclusa', description: 'Cada decisão tem um trace_id. Cada modelo tem uma justificativa. Cada execução pode ser auditada.' },
  { icon: '🔒', title: 'Privacidade por usuário', description: 'Cada usuário vê apenas seus próprios datasets, execuções e modelos. O filtro é feito no banco, não no frontend.' },
];

interface ProblemType {
  icon: string;
  title: string;
  description: string;
  metric: string;
  threshold: string;
}

const PROBLEM_TYPES: ProblemType[] = [
  { icon: '🏷️', title: 'Classificação', description: 'Quando a decisão é "sim ou não" — ex.: cliente vai cancelar?', metric: 'ROC AUC', threshold: '≥ 0,55' },
  { icon: '📊', title: 'Regressão', description: 'Quando a decisão é "quanto" — ex.: qual o preço de venda?', metric: 'R²', threshold: '≥ 0,10' },
  { icon: '📈', title: 'Série temporal', description: 'Quando a decisão é "quando" — ex.: qual a demanda do próximo mês?', metric: 'MAPE', threshold: '≤ 0,40' },
  { icon: '🔢', title: 'Contagem', description: 'Quando a decisão é "quantos" — ex.: quantos sinistros por cliente?', metric: 'Deviance', threshold: '0,5 – 2,0' },
];

export const Empresas: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="empresas-page">
      <PublicNav />
      <div className="empresas-container">

        <section className="empresas-hero">
          <div className="empresas-hero-badge">🏢 AutoSAGE para Empresas</div>
          <h1>
            Análise preditiva
            <br />
            <span className="highlight">sem cientista de dados.</span>
          </h1>
          <p className="empresas-hero-sub">
            Sua empresa tem dados, mas não tem um time de ciência de dados.
            O <strong>AutoSAGE</strong> transforma um arquivo CSV em predições
            auditáveis — <strong>sem código</strong>, <strong>sem instalação</strong>,
            <strong> com governança inclusa</strong>.
          </p>
          <div className="empresas-hero-actions">
            <button className="btn-emp-primary" onClick={() => navigate('/register')}>
              🚀 Criar conta
            </button>
            <button className="btn-emp-secondary" onClick={() => navigate('/login')}>
              🔐 Já tenho conta
            </button>
          </div>
          <div className="empresas-hero-stats">
            {FACTS.map((fact, i) => (
              <div key={i} className="empresa-stat">
                <span className="empresa-stat-icon">{fact.icon}</span>
                <span className="empresa-stat-value">{fact.value}</span>
                <span className="empresa-stat-label">{fact.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="empresas-section">
          <h2 className="empresas-section-title">O gargalo não é tecnológico</h2>
          <p className="empresas-section-sub">Ter dados não é o mesmo que tomar decisões com eles.</p>
          <div className="gargalos-grid">
            <div className="gargalo-card">
              <span className="gargalo-icon">👥</span>
              <h3>Sem time especializado</h3>
              <p>Contratar um cientista de dados é caro e demorado. Sem ele, os dados ficam parados em planilhas.</p>
            </div>
            <div className="gargalo-card">
              <span className="gargalo-icon">⏳</span>
              <h3>Do CSV ao modelo, meses</h3>
              <p>O caminho tradicional — coleta, limpeza, feature engineering, treino, avaliação, deploy — leva meses.</p>
            </div>
            <div className="gargalo-card">
              <span className="gargalo-icon">🔍</span>
              <h3>Sem explicação, sem confiança</h3>
              <p>Um modelo que ninguém entende é um modelo que ninguém usa. Em setores regulados, é risco jurídico.</p>
            </div>
          </div>
        </section>

        <section className="empresas-section">
          <h2 className="empresas-section-title">Como o AutoSAGE resolve</h2>
          <p className="empresas-section-sub">Um caminho direto, do arquivo à predição.</p>
          <div className="fluxo-grid">
            {[
              { icon: '📤', step: '1', title: 'Upload', desc: 'Suba um CSV com seus dados.' },
              { icon: '🔧', step: '2', title: 'Pipeline', desc: 'O ORC executa as 8 etapas automaticamente.' },
              { icon: '🤖', step: '3', title: 'Modelos', desc: '15 modelos testados, o melhor é escolhido.' },
              { icon: '🎯', step: '4', title: 'Predição', desc: 'Use o modelo em produção para prever.' },
            ].map((item, i) => (
              <div key={i} className="fluxo-card">
                <span className="fluxo-step">{item.step}</span>
                <span className="fluxo-icon">{item.icon}</span>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="empresas-section">
          <h2 className="empresas-section-title">4 tipos de problema, 4 métricas</h2>
          <p className="empresas-section-sub">Cada tipo tem sua métrica e seu limiar científico.</p>
          <div className="tipos-grid">
            {PROBLEM_TYPES.map((t, i) => (
              <div key={i} className="tipo-card">
                <span className="tipo-icon">{t.icon}</span>
                <h3>{t.title}</h3>
                <p className="tipo-desc">{t.description}</p>
                <div className="tipo-metric">
                  <span className="tipo-metric-label">Métrica:</span>
                  <span className="tipo-metric-value">{t.metric}</span>
                </div>
                <div className="tipo-threshold">
                  <span className="tipo-threshold-label">Limiar:</span>
                  <span className="tipo-threshold-value">{t.threshold}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="empresas-section">
          <h2 className="empresas-section-title">O que a empresa ganha</h2>
          <p className="empresas-section-sub">Benefícios ancorados no que a plataforma entrega.</p>
          <div className="beneficios-grid">
            {BENEFITS.map((b, i) => (
              <div key={i} className="beneficio-card">
                <span className="beneficio-icon">{b.icon}</span>
                <h3>{b.title}</h3>
                <p>{b.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="empresas-cta">
          <h2>Pronto para transformar seus dados em decisão?</h2>
          <p>Crie uma conta. Importe um CSV. Veja a predição em minutos.</p>
          <div className="empresas-cta-actions">
            <button className="btn-emp-primary" onClick={() => navigate('/register')}>
              🚀 Criar conta
            </button>
            <button className="btn-emp-secondary" onClick={() => navigate('/governanca')}>
              📖 Conhecer a governança
            </button>
          </div>
        </section>

      </div>
    </div>
  );
};

export default Empresas;